import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, DestroyRef, Component, ChangeDetectionStrategy, Input, HostBinding, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators, NgControl, NgForm, FormGroupDirective } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

/**
 * Control IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter$2 = 0;
class BaseControlComponent {
    get name() { return this._name; }
    set name(newValue) {
        this._name = newValue;
        this.nameUpdated?.();
    }
    get disabled() { return this._disabled; }
    set disabled(value) {
        this.setDisabledState(coerceBooleanProperty(value));
    }
    get required() { return this._required ? this._required : ((this.topNgControl?.control?.hasValidator(Validators.required) || this.topNgControl?.control?.hasValidator(Validators.requiredTrue)) ?? false); }
    set required(value) {
        this._required = coerceBooleanProperty(value);
    }
    get showError() {
        return !!(this.topNgControl?.invalid && (this.submitted || this.topNgControl?.touched));
    }
    constructor() {
        this.uniqueId = `${_uniqueIdCounter$2++}`;
        this.submitted = false;
        this._disabled = false;
        this._required = false;
        this.topNgControl = inject(NgControl, { optional: true });
        this.ngControl = inject(NgControl, { optional: true, self: true });
        this.parentForm = inject(NgForm, { optional: true });
        this.parentFormGroup = inject(FormGroupDirective, { optional: true });
        this.ref = inject(ChangeDetectorRef);
        this.destroyRef = inject(DestroyRef);
        if (this.ngControl != null) {
            // Setting the value accessor directly (instead of using
            // the providers) to avoid running into a circular import.
            this.ngControl.valueAccessor = this;
        }
        if (this.parentFormGroup) { // Reactive form
            this.setupSubmittedChangeDetector(this.parentFormGroup);
        }
        else if (this.parentForm) { // Template driven form
            this.setupSubmittedChangeDetector(this.parentForm);
        }
    }
    setupSubmittedChangeDetector(form) {
        form.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.submitted = true;
            this.ref.markForCheck(); // update showError
        });
        const originalResetForm = form.resetForm;
        form.resetForm = (value) => {
            originalResetForm.apply(form, [value]);
            this.submitted = false;
            this.ref.markForCheck(); // update showError
        };
    }
    setDisabledState(isDisabled) {
        this._disabled = isDisabled;
        this.disabledAttribute = this.disabled ? 'disabled' : undefined;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: BaseControlComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: BaseControlComponent, selector: "ng-component", inputs: { name: "name", tabindex: "tabindex", disabled: "disabled", required: "required" }, host: { properties: { "class.kb-submitted": "this.submitted", "attr.disabled": "this.disabledAttribute" } }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: BaseControlComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [], propDecorators: { name: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], submitted: [{
                type: HostBinding,
                args: ['class.kb-submitted']
            }], disabledAttribute: [{
                type: HostBinding,
                args: ['attr.disabled']
            }], disabled: [{
                type: Input
            }], required: [{
                type: Input
            }] } });

class InheritedControlValueAccessorComponent extends BaseControlComponent {
    ngAfterViewInit() {
        // Template driven forms first call the afterViewInit and then the registerOn function
        // Reactive forms first call the registerOn then the afterViewInit function
        if (this.formControlChangedBeforeViewInit) {
            this.valueAccessor.registerOnChange(this.formControlChangedBeforeViewInit);
        }
        if (this.formControlTouchedBeforeViewInit) {
            this.valueAccessor.registerOnTouched(this.formControlTouchedBeforeViewInit);
        }
        if (this.valueBeforeViewInit !== undefined) {
            this.valueAccessor.writeValue(this.valueBeforeViewInit);
        }
        this.valueAccessor.setDisabledState?.(this.disabled);
    }
    setDisabledState(isDisabled) {
        super.setDisabledState(isDisabled);
        if (this.valueAccessor) {
            this.valueAccessor.setDisabledState?.(isDisabled);
        }
    }
    convertTo(value) {
        return value;
    }
    convertFrom(value) {
        return value;
    }
    writeValue(value) {
        const convertedValue = this.convertTo(value);
        if (!this.valueAccessor) { // This only happens in case where valueAccessor is non static
            this.valueBeforeViewInit = convertedValue;
        }
        else {
            this.valueAccessor.writeValue(convertedValue);
        }
    }
    registerOnChange(onChange) {
        const onChangeConverted = (value) => {
            onChange(this.convertFrom(value));
        };
        if (this.valueAccessor) {
            this.valueAccessor.registerOnChange(onChangeConverted);
        }
        else {
            this.formControlChangedBeforeViewInit = onChangeConverted;
        }
    }
    registerOnTouched(onTouched) {
        if (this.valueAccessor) {
            this.valueAccessor.registerOnTouched(onTouched);
        }
        else {
            this.formControlTouchedBeforeViewInit = onTouched;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: InheritedControlValueAccessorComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: InheritedControlValueAccessorComponent, selector: "ng-component", usesInheritance: true, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: InheritedControlValueAccessorComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });

/**
 * Select IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter$1 = 0;
class ControlHintComponent {
    constructor() {
        this.id = `kb-label-hint-${_uniqueIdCounter$1++}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: ControlHintComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: ControlHintComponent, isStandalone: true, selector: "kb-control-hint", inputs: { showError: "showError" }, host: { properties: { "id": "this.id" } }, ngImport: i0, template: "<strong>\n  <ng-content *ngIf=\"!showError; else errorTemplate\" select=\"kb-hint\"></ng-content>\n  <ng-template #errorTemplate>\n    <ng-content select=\"kb-error\"></ng-content>\n  </ng-template>\n</strong>\n", styles: [":host{font-size:var(--kb-typography--size-extra-extra-small);line-height:var(--kb-typography--height-extra-extra-small)}:host strong{font-weight:inherit;display:inline-flex;gap:var(--kb-gap--inner-row)}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: ControlHintComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-control-hint', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [CommonModule], template: "<strong>\n  <ng-content *ngIf=\"!showError; else errorTemplate\" select=\"kb-hint\"></ng-content>\n  <ng-template #errorTemplate>\n    <ng-content select=\"kb-error\"></ng-content>\n  </ng-template>\n</strong>\n", styles: [":host{font-size:var(--kb-typography--size-extra-extra-small);line-height:var(--kb-typography--height-extra-extra-small)}:host strong{font-weight:inherit;display:inline-flex;gap:var(--kb-gap--inner-row)}\n"] }]
        }], propDecorators: { showError: [{
                type: Input
            }], id: [{
                type: HostBinding,
                args: ['id']
            }] } });

/**
 * ControlContainer IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 0;
class ControlContainerComponent {
    constructor() {
        this.uniqueId = `${_uniqueIdCounter++}`;
        this.labelId = `kb-control-container-label-${this.uniqueId}`;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: ControlContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.7", type: ControlContainerComponent, isStandalone: true, selector: "kb-control-container", inputs: { controlId: "controlId", showError: "showError" }, viewQueries: [{ propertyName: "hintElement", first: true, predicate: ["hintElement"], descendants: true }], ngImport: i0, template: "\n<div class=\"label-line\">\n  <label [id]=\"labelId\" [for]=\"controlId\">\n    <ng-content select=\"kb-label\"></ng-content>\n  </label>\n  <kb-control-hint #hintElement [showError]=\"showError\">\n    <ng-content select=\"kb-hint\" ngProjectAs=\"kb-hint\"></ng-content>\n    <ng-content select=\"kb-error\" ngProjectAs=\"kb-error\"></ng-content>\n  </kb-control-hint>\n</div>\n<ng-content></ng-content>\n", styles: [":host{display:inline-flex;transition:color var(--kb-speed--slow);flex-direction:column;justify-content:flex-end;gap:var(--kb-gap--outer-column--related) var(--kb-gap--outer-row--related);width:100%}:host .label-line{display:flex;justify-content:space-between;align-items:baseline;gap:var(--kb-gap--inner-row);padding:0 .125rem}:host .label-line kb-control-hint{flex-shrink:0}\n"], dependencies: [{ kind: "component", type: ControlHintComponent, selector: "kb-control-hint", inputs: ["showError"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.7", ngImport: i0, type: ControlContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'kb-control-container', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [ControlHintComponent], template: "\n<div class=\"label-line\">\n  <label [id]=\"labelId\" [for]=\"controlId\">\n    <ng-content select=\"kb-label\"></ng-content>\n  </label>\n  <kb-control-hint #hintElement [showError]=\"showError\">\n    <ng-content select=\"kb-hint\" ngProjectAs=\"kb-hint\"></ng-content>\n    <ng-content select=\"kb-error\" ngProjectAs=\"kb-error\"></ng-content>\n  </kb-control-hint>\n</div>\n<ng-content></ng-content>\n", styles: [":host{display:inline-flex;transition:color var(--kb-speed--slow);flex-direction:column;justify-content:flex-end;gap:var(--kb-gap--outer-column--related) var(--kb-gap--outer-row--related);width:100%}:host .label-line{display:flex;justify-content:space-between;align-items:baseline;gap:var(--kb-gap--inner-row);padding:0 .125rem}:host .label-line kb-control-hint{flex-shrink:0}\n"] }]
        }], propDecorators: { controlId: [{
                type: Input,
                args: [{ required: true }]
            }], showError: [{
                type: Input
            }], hintElement: [{
                type: ViewChild,
                args: ['hintElement']
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { BaseControlComponent, ControlContainerComponent, ControlHintComponent, InheritedControlValueAccessorComponent };
